import type { CalloutMode } from '@tv-casino/shared';
import type { AmbienceLevel, SoundCue } from '@tv-casino/viewmodels/lib/soundCues.js';
import {
  DEFAULT_CALLOUT_MODE,
  DEFAULT_VOLUMES,
  readCalloutMode as readMode,
  readVolumes,
  type VolumePrefs,
} from '@tv-casino/viewmodels/lib/deviceAudio.js';

/**
 * The TV's sound cues.
 *
 * Chips, cards, the shuffle and the dice are recorded samples — Kenney's CC0
 * casino pack, transcoded to AAC under `public/sounds/` so Safari and cast
 * devices decode them too. They load the first time sound is switched on. The
 * roulette ball has no recording, so it stays synthesised, and every sampled
 * cue that has a synth version plays that until its samples land (or if they
 * never do).
 *
 * Under the cues runs a casino-floor bed (a CC0 Freesound field recording,
 * baked into a seamless loop). It is loudest between hands, drops back while
 * bets go down, and sits low under the cards once a hand is live.
 *
 * Sound is off until someone turns it on. That is not only taste — a browser
 * will not let a page make noise before someone has clicked it, so the toggle
 * doubles as the gesture that unlocks the audio context. Starting muted means
 * the first cue after the toggle actually plays, instead of failing silently.
 *
 * The mute is remembered per scope, not per site: the TV page and a player
 * page opened in the same browser are two seats that must not unmute each
 * other. `table` is the felt's own switch (the TV's Sound button, or the
 * host's remote flip of it); `device` is one phone's "Your audio". Without
 * the split, a browser holding both pages plays the dealer only when the
 * host's TV switch is on — the player's own control could not win.
 */

export type SoundScope = 'table' | 'device';

const SOUND_KEYS: Record<SoundScope, string> = {
  table: 'tv-casino:sound',
  device: 'tv-casino:sound:device',
};

let scope: SoundScope = 'table';

/** Say which seat this page is, before its first audio control reads storage. */
export function setSoundScope(next: SoundScope): void {
  scope = next;
}

const soundKey = (): string => SOUND_KEYS[scope];
const VOLUME_KEY = 'tv-casino:volume';

/** The cue names and the floor level are the package's contract, so a native
 * engine can drive the same soundtrack; this file is the browser's player. */
export type { AmbienceLevel, SoundCue };

/** Recorded variants per cue; one is picked at random so a deal is not a loop. */
const SAMPLES: Partial<Record<SoundCue, readonly string[]>> = {
  chip: ['chip-lay-1', 'chip-lay-2', 'chip-lay-3'],
  payout: ['chips-stack-1', 'chips-stack-2', 'chips-stack-3'],
  sweep: ['chips-handle-1', 'chips-handle-2', 'chips-handle-3'],
  dice: ['dice-throw-1', 'dice-throw-2', 'dice-throw-3'],
  card: ['card-slide-1', 'card-slide-2', 'card-slide-3', 'card-slide-4'],
  cardFlip: ['card-place-1', 'card-place-2', 'card-place-3'],
  shuffle: ['card-shuffle'],
};

const AMBIENCE_FILE = 'ambience-casino-floor';
const AMBIENCE_GAIN: Record<AmbienceLevel, number> = { idle: 0.5, betting: 0.28, play: 0.12 };

let ctx: AudioContext | null = null;
let master: GainNode | null = null;

/** Full-scale master level; the host's volume slider scales it down. */
const MASTER_GAIN = 0.5;
/** The host's sliders from the phone, as 0–1. */
let soundVolume = 1;
let ambienceVolume = 1;

const unit = (percent: number): number =>
  Number.isFinite(percent) ? Math.min(1, Math.max(0, percent / 100)) : 1;

/** Apply the host's volume and background sliders (each 0–100), gliding. */
export function setVolumes(sound: number, ambience: number): void {
  soundVolume = unit(sound);
  ambienceVolume = unit(ambience);
  if (ctx && master) {
    try {
      master.gain.setTargetAtTime(MASTER_GAIN * soundVolume, ctx.currentTime, 0.15);
    } catch {
      // A context that cannot automate keeps its old level.
    }
  }
  applyAmbience();
}
/** A device's own levels, 0-100 each. */
/**
 * This device's remembered levels and its dealer's chattiness. The shape, the
 * defaults and the clamping live in `@tv-casino/viewmodels/lib/deviceAudio` —
 * the phone shells keep the same prefs, and a seat that sounded one way in a
 * browser must not sound another way in the app. This file only owns where
 * they are stored.
 */
export type { VolumePrefs };
export { DEFAULT_VOLUMES };

export function readVolumePrefs(): VolumePrefs {
  try {
    const raw = window.localStorage.getItem(VOLUME_KEY);
    return readVolumes(raw ? (JSON.parse(raw) as unknown) : null);
  } catch {
    // Unreadable or corrupt storage: start from the defaults.
    return DEFAULT_VOLUMES;
  }
}

/**
 * Remember this device's levels and apply them to what is playing now. The
 * dealer's level is remembered here with the rest but applied by `voice.ts`,
 * which owns that fader; the caller hands it on.
 */
export function writeVolumePrefs(next: VolumePrefs): VolumePrefs {
  const prefs = readVolumes(next);
  try {
    window.localStorage.setItem(VOLUME_KEY, JSON.stringify(prefs));
  } catch {
    // A locked-down browser keeps the levels for this session only.
  }
  setVolumes(prefs.sound, prefs.ambience);
  return prefs;
}

const CALLOUTS_KEY = 'tv-casino:callouts';

/**
 * This device's own dealer level — off, the decisions, or every call. A phone
 * keeps its own here rather than following the room: a player on headphones
 * picks how chatty their dealer is, whatever the TV is set to. Starts at the
 * same "decisions" the room starts at.
 */
export function readCalloutMode(): CalloutMode {
  try {
    return readMode(window.localStorage.getItem(CALLOUTS_KEY));
  } catch {
    return DEFAULT_CALLOUT_MODE;
  }
}

/** Remember this device's dealer level. */
export function writeCalloutMode(mode: CalloutMode): void {
  try {
    window.localStorage.setItem(CALLOUTS_KEY, mode);
  } catch {
    // A locked-down browser keeps the choice for this session only.
  }
}

let roll: AudioBufferSourceNode | null = null;

const audioCtor = (): typeof AudioContext | null => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ?? null;
};

/** Is a Web Audio context available at all? jsdom and old Safari say no. */
export const soundSupported = (): boolean => audioCtor() !== null;

export function soundEnabled(): boolean {
  try {
    return window.localStorage.getItem(soundKey()) === 'on';
  } catch {
    return false;
  }
}

export function setSoundEnabled(on: boolean): void {
  try {
    window.localStorage.setItem(soundKey(), on ? 'on' : 'off');
  } catch {
    // A locked-down browser still gets sound for this session.
  }
  if (!on) stopRoll();
  else void resume();
  applyAmbience();
}

/**
 * The context, built on first use. Browsers hand it over suspended until a
 * gesture resumes it, which is exactly what the toggle does.
 *
 * A browser that refuses to build one — an audio stack under restriction, a
 * partial implementation — gets a silent table, never a broken one. Nothing
 * on this page is worth taking the felt down for.
 */
let audioBroken = false;
function audio(): AudioContext | null {
  if (ctx) return ctx;
  if (audioBroken) return null;
  const Ctor = audioCtor();
  if (!Ctor) return null;
  try {
    // Safari 17+: a 'playback' session plays through the iPhone's silent
    // switch. Without it, a phone mirrored to the TV mutes the whole table.
    const session = (navigator as { audioSession?: { type: string } }).audioSession;
    if (session) session.type = 'playback';
  } catch {
    // An older Safari keeps the ambient session.
  }
  try {
    const next = new Ctor();
    master = next.createGain();
    master.gain.value = MASTER_GAIN * soundVolume;
    master.connect(next.destination);
    ctx = next;
    loadSamples(next);
    loadAmbience(next);
    return ctx;
  } catch {
    audioBroken = true;
    master = null;
    return null;
  }
}

/**
 * Resume after the gesture that turned sound on. Safe to call repeatedly.
 * Anything short of running counts: WebKit parks a context in a non-standard
 * `interrupted` state after a phone call or Siri, and only a resume brings it
 * back. The resume call itself runs before any await, so it lands inside the
 * gesture that asked for it.
 */
export async function resume(): Promise<void> {
  try {
    const c = audio();
    const state: string | undefined = c?.state;
    if (c && state !== 'running' && state !== 'closed') await c.resume().catch(() => {});
  } catch {
    // Sound is a nicety; a table that cannot make noise still deals.
  }
}

/**
 * Sound remembered as on survives a reload, but the audio context does not:
 * Safari only lets a context start inside a tap, so a reloaded TV would show
 * "Sound" and play nothing until someone toggled it off and on again. Any tap
 * or key on the page resumes it instead — and resumes it again after a call
 * or Siri interrupts it. Armed once per page; a muted table ignores it.
 */
let unlockArmed = false;
export function armGestureUnlock(): void {
  if (unlockArmed || typeof document === 'undefined') return;
  unlockArmed = true;
  const onGesture = () => {
    if (soundEnabled()) void resume();
  };
  for (const type of ['touchend', 'pointerup', 'keydown'] as const) {
    document.addEventListener(type, onGesture, { capture: true, passive: true });
  }
}

/** White noise, one second of it, reused by every rolling ball. */
let noise: AudioBuffer | null = null;
function noiseBuffer(c: AudioContext): AudioBuffer {
  if (noise && noise.sampleRate === c.sampleRate) return noise;
  const buffer = c.createBuffer(1, c.sampleRate, c.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < data.length; i += 1) data[i] = Math.random() * 2 - 1;
  noise = buffer;
  return buffer;
}

/** Decoded samples by cue. Filled in the background; empty until then. */
const bank = new Map<SoundCue, AudioBuffer[]>();
let loading = false;

function loadSamples(c: AudioContext): void {
  if (loading || typeof fetch !== 'function') return;
  loading = true;
  for (const [cue, names] of Object.entries(SAMPLES) as [SoundCue, readonly string[]][]) {
    void Promise.all(
      names.map(async (name) => {
        try {
          const res = await fetch(`/sounds/${name}.m4a`);
          if (!res.ok) return null;
          return await c.decodeAudioData(await res.arrayBuffer());
        } catch {
          // A missing or undecodable file costs that variant, not the table.
          return null;
        }
      }),
    ).then((buffers) => {
      const ready = buffers.filter((b): b is AudioBuffer => b !== null);
      if (ready.length) bank.set(cue, ready);
    });
  }
}

/** The floor bed: one looping source through its own fader. */
let bed: { source: AudioBufferSourceNode; gain: GainNode } | null = null;
let bedBuffer: AudioBuffer | null = null;
let bedLoading = false;
/** What the table last asked for; null when no table is on screen. */
let wantedAmbience: AmbienceLevel | null = null;

function loadAmbience(c: AudioContext): void {
  if (bedLoading || typeof fetch !== 'function') return;
  bedLoading = true;
  void (async () => {
    try {
      const res = await fetch(`/sounds/${AMBIENCE_FILE}.m4a`);
      if (!res.ok) return;
      bedBuffer = await c.decodeAudioData(await res.arrayBuffer());
      applyAmbience();
    } catch {
      // No bed; the cues still play.
    }
  })();
}

/**
 * The context and master fader, for the dealer's voice (`voice.ts`) to play
 * through: a second context on a cast device is wasted memory and a second
 * autoplay unlock. Null while sound is off or the browser has no audio.
 */
export function audioGraph(): { ctx: AudioContext; master: GainNode } | null {
  if (!soundEnabled()) return null;
  const c = audio();
  return c && master ? { ctx: c, master } : null;
}

/** How far the floor dips while the dealer speaks. */
const DUCK_GAIN = 0.35;
let ducked = false;

/** Dip the floor under the dealer's voice, or bring it back. A glide, never a cut. */
export function duckAmbience(on: boolean): void {
  if (ducked === on) return;
  ducked = on;
  applyAmbience(0.12);
}

/** Set the floor's level for what the table is doing, or null to silence it. */
export function setAmbience(level: AmbienceLevel | null): void {
  wantedAmbience = level;
  applyAmbience();
}

/**
 * Start the bed if it should be running, then glide to the wanted level. It
 * starts at a random point in the loop so a reload never sounds like a rewind.
 */
function applyAmbience(glide = 0.6): void {
  if (wantedAmbience === null || !soundEnabled()) {
    stopAmbience();
    return;
  }
  const c = audio();
  if (!c || !master || !bedBuffer) return;
  try {
    if (!bed) {
      const gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, c.currentTime);
      const source = c.createBufferSource();
      source.buffer = bedBuffer;
      source.loop = true;
      source.connect(gain).connect(master);
      source.start(c.currentTime, Math.random() * bedBuffer.duration);
      bed = { source, gain };
    }
    bed.gain.gain.cancelScheduledValues(c.currentTime);
    bed.gain.gain.setTargetAtTime(
      AMBIENCE_GAIN[wantedAmbience] * ambienceVolume * (ducked ? DUCK_GAIN : 1),
      c.currentTime,
      glide,
    );
  } catch {
    audioBroken = true;
  }
}

function stopAmbience(): void {
  if (!bed) return;
  const { source } = bed;
  bed = null;
  try {
    source.stop();
  } catch {
    // Already stopped.
  }
}

/** Play one recorded variant of `cue`, a touch detuned. False if none has loaded. */
function sample(c: AudioContext, out: GainNode, cue: SoundCue, at: number): boolean {
  const variants = bank.get(cue);
  if (!variants?.length) return false;
  const source = c.createBufferSource();
  source.buffer = variants[Math.floor(Math.random() * variants.length)]!;
  source.playbackRate.value = 0.96 + Math.random() * 0.08;
  source.connect(out);
  source.start(at);
  return true;
}

const ping = (c: AudioContext, out: GainNode, freq: number, at: number, len: number, peak: number, type: OscillatorType = 'triangle') => {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, at);
  gain.gain.setValueAtTime(0.0001, at);
  gain.gain.exponentialRampToValueAtTime(peak, at + 0.008);
  gain.gain.exponentialRampToValueAtTime(0.0001, at + len);
  osc.connect(gain).connect(out);
  osc.start(at);
  osc.stop(at + len + 0.02);
};

/** One cue. A no-op when sound is off, unsupported, or still suspended. */
export function play(cue: SoundCue): void {
  if (!soundEnabled()) return;
  const c = audio();
  if (!c || !master) return;
  void resume();
  const now = c.currentTime;

  try {
    if (cue === 'bigWin' || cue === 'jackpot') winGraph(cue, c, master, now);
    else if (!sample(c, master, cue, now)) cueGraph(cue, c, master, now);
  } catch {
    audioBroken = true;
  }
}

/**
 * A win worth standing up for: a bright fanfare over a cascade of chip
 * stacks. The chips are the recorded payout samples, staggered so they pour;
 * before those load the fanfare plays alone. A jackpot climbs an octave
 * higher, holds its top note, and rains more chips with a shimmer on top.
 */
function winGraph(cue: 'bigWin' | 'jackpot', c: AudioContext, out: GainNode, now: number): void {
  const jackpot = cue === 'jackpot';
  // C major, up the arpeggio: C5 E5 G5 C6, and on to E6 G6 C7 for a jackpot.
  const notes = jackpot
    ? [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98, 2093]
    : [523.25, 659.25, 783.99, 1046.5];
  const step = jackpot ? 0.075 : 0.09;
  notes.forEach((freq, i) => {
    const at = now + i * step;
    const last = i === notes.length - 1;
    const len = last ? (jackpot ? 1.4 : 0.7) : 0.26;
    ping(c, out, freq, at, len, 0.12);
    // A sine an octave down gives the brass some body.
    ping(c, out, freq / 2, at, len, 0.06, 'sine');
  });
  if (jackpot) {
    // The held chord under the top note.
    const hold = now + notes.length * step;
    for (const freq of [1046.5, 1318.51, 1567.98]) ping(c, out, freq, hold, 1.6, 0.05, 'sine');
    // Sparkle: quick high pings scattered over the chord.
    for (let i = 0; i < 10; i += 1) {
      ping(c, out, 2_400 + Math.random() * 2_400, hold + i * 0.11 + Math.random() * 0.05, 0.12, 0.035, 'sine');
    }
  }
  const pours = jackpot ? 7 : 3;
  for (let i = 0; i < pours; i += 1) {
    sample(c, out, 'payout', now + 0.05 + i * (jackpot ? 0.16 : 0.2));
  }
}

/** The synth stand-ins. Cards and the shuffle have none: they wait for samples. */
function cueGraph(cue: SoundCue, c: AudioContext, master: GainNode, now: number): void {
  switch (cue) {
    // A chip landing on felt: a short, dry click with a little body.
    case 'chip':
      ping(c, master, 880, now, 0.06, 0.16, 'square');
      ping(c, master, 240, now + 0.01, 0.09, 0.1);
      break;
    // The ball dropping out of the track into a pocket: a hard clack, then
    // the two or three bounces before it settles.
    case 'ballDrop':
      ping(c, master, 1_400, now, 0.05, 0.22, 'square');
      ping(c, master, 900, now + 0.09, 0.05, 0.13, 'square');
      ping(c, master, 700, now + 0.16, 0.05, 0.08, 'square');
      break;
    // Two dice hitting the back wall: a hard clatter, then the settle.
    case 'dice':
      ping(c, master, 620, now, 0.04, 0.2, 'square');
      ping(c, master, 480, now + 0.05, 0.04, 0.16, 'square');
      ping(c, master, 380, now + 0.11, 0.05, 0.12, 'square');
      ping(c, master, 300, now + 0.19, 0.06, 0.07, 'square');
      break;
    // Chips paid out: a rising third, the house being generous.
    case 'payout':
      ping(c, master, 523.25, now, 0.22, 0.16);
      ping(c, master, 659.25, now + 0.08, 0.24, 0.16);
      ping(c, master, 783.99, now + 0.16, 0.34, 0.14);
      break;
    // Losing stacks swept off the layout: a low, dull scrape down.
    case 'sweep': {
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(220, now);
      osc.frequency.exponentialRampToValueAtTime(70, now + 0.35);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.09, now + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.4);
      osc.connect(gain).connect(master);
      osc.start(now);
      osc.stop(now + 0.42);
      break;
    }
  }
}

/**
 * The ball in the track: filtered noise that rises as it is launched, then
 * falls away over `ms` as the ball loses speed. `ms` is the server's own
 * settle beat, so the sound runs out exactly when the ball lands.
 */
export function startRoll(ms: number): void {
  if (!soundEnabled()) return;
  const c = audio();
  if (!c || !master) return;
  void resume();
  stopRoll();
  try {
    rollGraph(c, master, ms);
  } catch {
    audioBroken = true;
  }
}

function rollGraph(c: AudioContext, master: GainNode, ms: number): void {
  const source = c.createBufferSource();
  source.buffer = noiseBuffer(c);
  source.loop = true;
  const filter = c.createBiquadFilter();
  filter.type = 'bandpass';
  filter.Q.value = 6;
  const gain = c.createGain();

  const now = c.currentTime;
  const end = now + ms / 1_000;
  // A rolling ball drops in pitch as it slows, and quietens as it does.
  filter.frequency.setValueAtTime(2_600, now);
  filter.frequency.exponentialRampToValueAtTime(700, end);
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.08, now + 0.25);
  gain.gain.exponentialRampToValueAtTime(0.0001, end);

  source.connect(filter).connect(gain).connect(master);
  source.start(now);
  source.stop(end + 0.05);
  roll = source;
}

/** Cut the roll short — the spin settled, or sound was switched off. */
export function stopRoll(): void {
  if (!roll) return;
  try {
    roll.stop();
  } catch {
    // Already stopped; nothing to do.
  }
  roll = null;
}
